import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Save, Loader2, PencilLine, Building2, Home } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { propertyCostsAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errors';
import { buildCostTypeOptions, NON_PNL_CODES, PERCENTAGE_CODES } from '../lib/costTaxonomy';
import { costRowLabel, costSupplier, costAmount } from '../lib/costRowLabel';
import { useEntityStore } from '../store';

// Derived from canonical registry (costTaxonomy.js), not hand-maintained.
const COST_TYPES = buildCostTypeOptions();
const NON_PNL_TYPES = NON_PNL_CODES;
const PERCENTAGE_TYPES = PERCENTAGE_CODES;

const BASES = [
  { value: 'net', label: 'Net Revenue' },
  { value: 'gross', label: 'Booking Volume' },
  { value: 'accommodation', label: 'Accommodation Only' },
];

const FREQUENCIES = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annual', label: 'Annual' },
  { value: 'per_stay', label: 'Per Stay' },
];

const CURRENCIES = [
  { value: 'GBP', label: 'GBP' },
  { value: 'EUR', label: 'EUR' },
];

function monthlyEquivalent(rawAmount, frequency) {
  // Amounts arrive as strings ("60.00"); adding strings gave "£NaN/mo".
  const amount = costAmount(rawAmount);
  if (frequency === 'quarterly') return amount / 3;
  if (frequency === 'annual') return amount / 12;
  if (frequency === 'per_stay') return null; // varies by month, no fixed equivalent
  if (frequency === 'none') return 0; // one-off, no monthly run rate
  return amount;
}

function getAvailableCostTypes(tenure) {
  if (!tenure) return COST_TYPES;
  return COST_TYPES.filter(t => t.tenure === null || t.tenure === tenure);
}

// ---------------------------------------------------------------------------
// Tenure selector
// ---------------------------------------------------------------------------

function TenureSelector({ propertyId, tenure, onTenureChange, isDerived, onDerivedCleared }) {
  const [saving, setSaving] = useState(false);

  const handleChange = async (val) => {
    setSaving(true);
    try {
      await propertyCostsAPI.setTenure(propertyId, val);
      onTenureChange(val);
      if (onDerivedCleared) onDerivedCleared();
      toast.success(`Tenure set to ${val}`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <p className="text-xs font-medium text-muted-foreground">Tenure:</p>
      <div className="flex gap-1">
        <Button
          variant={tenure === 'leased' ? 'default' : 'outline'}
          size="sm"
          className="h-7 text-xs"
          onClick={() => handleChange('leased')}
          disabled={saving}
        >
          <Building2 className="h-3 w-3 mr-1" /> Leased
        </Button>
        <Button
          variant={tenure === 'owned' ? 'default' : 'outline'}
          size="sm"
          className="h-7 text-xs"
          onClick={() => handleChange('owned')}
          disabled={saving}
        >
          <Home className="h-3 w-3 mr-1" /> Owned
        </Button>
      </div>
      {saving && <Loader2 className="h-3 w-3 animate-spin" />}
      {isDerived && (
        <span className="text-[10px] text-muted-foreground">(auto-derived from region, click to confirm)</span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cost row (existing)
// ---------------------------------------------------------------------------

// Rent paid to one of our own entities (Fan, 2026-10-24): the Property
// Portfolio shows the properties together, so this rent is left out of its
// figures and only counts in the entity view. The flag comes from the API
// (`intra_group_rent`), set by choosing the landlord below.
function LandlordPicker({ cost, propertyId, onChanged }) {
  const entities = useEntityStore((st) => st.entities) || [];
  const [saving, setSaving] = useState(false);
  const options = entities.filter((e) => e.type && !e.retired && !e.merged_into);
  const value = cost.landlord_entity_id || '';
  const handle = async (ev) => {
    setSaving(true);
    try {
      await propertyCostsAPI.updateCost(propertyId, cost.id, { landlord_entity_id: ev.target.value });
      toast.success(ev.target.value ? 'Marked as rent to one of our entities' : 'Marked as rent to an outside landlord');
      onChanged();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <select
      className="ml-1.5 h-6 rounded border border-input bg-background px-1 text-[11px] text-muted-foreground"
      value={value}
      onChange={handle}
      disabled={saving}
      title="Who receives this rent? Rent to one of our entities is left out of Property Portfolio and Overall, and kept in the entity view."
      data-testid={`landlord-picker-${cost.id}`}
    >
      <option value="">Paid to: outside landlord</option>
      {options.map((e) => (
        <option key={e.type} value={e.type}>Paid to: {e.name} (ours)</option>
      ))}
    </select>
  );
}

function CostRow({ cost, propertyId, onDeleted }) {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await propertyCostsAPI.deleteCost(propertyId, cost.id);
      toast.success('Cost entry ended');
      onDeleted();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDeleting(false);
    }
  };

  const typeLabel = COST_TYPES.find((t) => t.value === cost.cost_type)?.label || cost.cost_type;
  const label = costRowLabel(cost, typeLabel);
  const supplier = costSupplier(cost);
  const freqLabel = cost.frequency === 'none' ? 'One-off' : (FREQUENCIES.find((f) => f.value === cost.frequency)?.label || cost.frequency);
  const monthly = monthlyEquivalent(cost.amount, cost.frequency);
  const isEnded = cost.effective_to != null;
  const isNonPnl = NON_PNL_TYPES.has(cost.cost_type);
  const isInternalRent = !!cost.intra_group_rent;
  const isRent = cost.cost_type === 'rent';
  const isPerStay = cost.frequency === 'per_stay';
  const isPct = PERCENTAGE_TYPES.has(cost.cost_type);
  const basisLabel = isPct ? (BASES.find(b => b.value === cost.basis)?.label || cost.basis) : null;

  return (
    <tr className={`text-sm ${isEnded ? 'opacity-50' : ''} ${isInternalRent ? 'text-muted-foreground' : ''}`} data-internal-rent={isInternalRent ? 'true' : undefined}>
      <td className="py-1.5 pr-3">
        <Badge variant="outline" className="text-xs" title={cost.notes || typeLabel}>
          {label}
        </Badge>
        {supplier && (
          <span className="ml-1.5 text-xs text-muted-foreground" title={cost.notes || ''}>{supplier}</span>
        )}
        {isNonPnl && (
          <span className="ml-1 text-[10px] text-muted-foreground">(cash only)</span>
        )}
        {isRent && !isEnded && (
          <LandlordPicker cost={cost} propertyId={propertyId} onChanged={onDeleted} />
        )}
        {isInternalRent && (
          <span
            className="ml-1.5 text-[10px] text-muted-foreground"
            title="Rent between our own entities. Left out of Property Portfolio and Overall; it counts in the entity view only."
          >
            (between our entities: entity view only)
          </span>
        )}
      </td>
      <td className="py-1.5 pr-3 font-mono text-right">
        {isPct
          ? <>{(cost.rate * 100).toFixed(1)}%</>
          : <>{formatCurrency(cost.amount, cost.currency)}{isPerStay ? '/stay' : ''}</>
        }
      </td>
      <td className="py-1.5 pr-3 text-muted-foreground">
        {isPct ? basisLabel : freqLabel}
      </td>
      <td className="py-1.5 pr-3 font-mono text-right text-muted-foreground">
        {isPct
          ? <span className="text-[10px]">varies by month</span>
          : isPerStay
            ? <span className="text-[10px]">varies by stays</span>
            : <>{formatCurrency(monthly, cost.currency)}/mo</>
        }
      </td>
      <td className="py-1.5 pr-3 text-xs text-muted-foreground">
        {cost.effective_from}{isEnded ? ` - ${cost.effective_to}` : ' - ongoing'}
      </td>
      <td className="py-1.5">
        {!isEnded && !cost.mortgage_schedule && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
          </Button>
        )}
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Add cost form (tenure-gated)
// ---------------------------------------------------------------------------

function AddCostForm({ propertyId, propertyCurrency, tenure, onCreated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const available = getAvailableCostTypes(tenure);
  // Exclude mortgage types from manual add (use mortgage form instead)
  const manualTypes = available.filter(t =>
    t.value !== 'mortgage_interest' && t.value !== 'mortgage_principal'
  );
  const defaultType = manualTypes[0]?.value || 'other';

  const [form, setForm] = useState({
    cost_type: defaultType,
    amount: '',
    currency: propertyCurrency || 'GBP',
    frequency: 'monthly',
    effective_from: new Date().toISOString().slice(0, 10),
    notes: '',
    rate: '',
    basis: 'net',
  });

  if (!open) {
    return (
      <Button variant="outline" size="sm" className="mt-2" onClick={() => setOpen(true)}>
        <Plus className="h-3 w-3 mr-1" /> Add Cost
      </Button>
    );
  }

  const isPctType = PERCENTAGE_TYPES.has(form.cost_type);

  const handleSave = async () => {
    if (isPctType) {
      const rateVal = parseFloat(form.rate);
      if (!form.rate || isNaN(rateVal) || rateVal <= 0 || rateVal > 100) {
        toast.error('Enter a valid rate (1-100%)');
        return;
      }
      setSaving(true);
      try {
        await propertyCostsAPI.createCost(propertyId, {
          cost_type: form.cost_type,
          rate: rateVal / 100,  // UI shows %, API expects decimal
          basis: form.basis,
          amount: 0,
          currency: form.currency,
          frequency: 'monthly',
          effective_from: form.effective_from,
          notes: form.notes || null,
        });
        toast.success('Commission cost created');
        setOpen(false);
        setForm({ ...form, rate: '', notes: '' });
        onCreated();
      } catch (err) {
        toast.error(getErrorMessage(err));
      } finally {
        setSaving(false);
      }
    } else {
      if (!form.amount || isNaN(parseFloat(form.amount))) {
        toast.error('Enter a valid amount');
        return;
      }
      setSaving(true);
      try {
        await propertyCostsAPI.createCost(propertyId, {
          ...form,
          amount: parseFloat(form.amount),
          rate: undefined,
          basis: undefined,
        });
        toast.success('Cost entry created');
        setOpen(false);
        setForm({ ...form, amount: '', notes: '' });
        onCreated();
      } catch (err) {
        toast.error(getErrorMessage(err));
      } finally {
        setSaving(false);
      }
    }
  };

  const isPerStayType = form.cost_type === 'manager_cleaning_per_stay';
  const availableFrequencies = isPerStayType
    ? FREQUENCIES.filter(f => f.value === 'per_stay')
    : FREQUENCIES.filter(f => f.value !== 'per_stay');

  const handleTypeChange = (v) => {
    if (v === 'manager_cleaning_per_stay') {
      setForm({ ...form, cost_type: v, frequency: 'per_stay' });
    } else if (PERCENTAGE_TYPES.has(v)) {
      setForm({ ...form, cost_type: v, frequency: 'monthly' });
    } else if (form.frequency === 'per_stay') {
      setForm({ ...form, cost_type: v, frequency: 'monthly' });
    } else {
      setForm({ ...form, cost_type: v });
    }
  };

  return (
    <div className="mt-2 p-3 border rounded-lg bg-muted/30 space-y-3">
      {isPerStayType && (
        <p className="text-[10px] text-muted-foreground">
          Per-stay rate: the amount the manager charges per booking. Monthly cost = rate x stays that month.
        </p>
      )}
      {isPctType && (
        <p className="text-[10px] text-muted-foreground">
          Percentage of revenue: the manager takes a % of monthly booking revenue. Cost varies by month.
        </p>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div>
          <Label className="text-xs">Type</Label>
          <Select value={form.cost_type} onValueChange={handleTypeChange}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {manualTypes.map((t) => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {isPctType ? (
          <>
            <div>
              <Label className="text-xs">Rate (%)</Label>
              <Input
                type="number"
                step="0.1"
                className="h-8 text-xs font-mono"
                value={form.rate}
                onChange={(e) => setForm({ ...form, rate: e.target.value })}
                placeholder="e.g. 15"
              />
            </div>
            <div>
              <Label className="text-xs">Basis</Label>
              <Select value={form.basis} onValueChange={(v) => setForm({ ...form, basis: v })}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BASES.map((b) => (
                    <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </>
        ) : (
          <>
            <div>
              <Label className="text-xs">Amount</Label>
              <Input
                type="number"
                step="0.01"
                className="h-8 text-xs font-mono"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder="0.00"
              />
            </div>
            <div>
              <Label className="text-xs">{isPerStayType ? 'Rate per' : 'Frequency'}</Label>
              <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })} disabled={isPerStayType}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {availableFrequencies.map((f) => (
                    <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </>
        )}
        <div>
          <Label className="text-xs">Currency</Label>
          <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CURRENCIES.map((c) => (
                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">Effective From</Label>
          <Input
            type="date"
            className="h-8 text-xs"
            value={form.effective_from}
            onChange={(e) => setForm({ ...form, effective_from: e.target.value })}
          />
        </div>
        <div>
          <Label className="text-xs">Notes (optional)</Label>
          <Input
            className="h-8 text-xs"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="e.g. landlord name"
          />
        </div>
      </div>
      <div className="flex gap-2">
        <Button size="sm" className="h-7 text-xs" onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Save className="h-3 w-3 mr-1" />}
          Save
        </Button>
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mortgage schedule form (owned properties only)
// ---------------------------------------------------------------------------

function MortgageScheduleForm({ propertyId, propertyCurrency, onCreated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState({
    principal: '',
    annual_rate: '',
    term_months: '',
    monthly_payment: '',
    start_date: '',
    currency: propertyCurrency || 'EUR',
    notes: '',
  });

  if (!open) {
    return (
      <Button variant="outline" size="sm" className="mt-2" onClick={() => setOpen(true)}>
        <Plus className="h-3 w-3 mr-1" /> Set Up Mortgage
      </Button>
    );
  }

  const handlePreview = async () => {
    if (!form.principal || !form.annual_rate || !form.term_months || !form.monthly_payment || !form.start_date) {
      toast.error('Fill in all loan fields');
      return;
    }
    setPreviewing(true);
    try {
      const res = await propertyCostsAPI.previewMortgage(propertyId, {
        principal: parseFloat(form.principal),
        annual_rate: parseFloat(form.annual_rate) / 100, // UI shows %, API expects decimal
        term_months: parseInt(form.term_months, 10),
        monthly_payment: parseFloat(form.monthly_payment),
        start_date: form.start_date,
        currency: form.currency,
        notes: form.notes || null,
      });
      setPreview(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPreviewing(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await propertyCostsAPI.createMortgageSchedule(propertyId, {
        principal: parseFloat(form.principal),
        annual_rate: parseFloat(form.annual_rate) / 100,
        term_months: parseInt(form.term_months, 10),
        monthly_payment: parseFloat(form.monthly_payment),
        start_date: form.start_date,
        currency: form.currency,
        notes: form.notes || null,
      });
      toast.success('Mortgage schedule created');
      setOpen(false);
      setPreview(null);
      onCreated();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-2 p-3 border rounded-lg bg-muted/30 space-y-3">
      <p className="text-xs font-medium">Mortgage Loan Terms</p>
      <p className="text-[10px] text-muted-foreground">
        Enter loan details once. The system computes monthly interest (P&L cost) and principal (cash only) for each month.
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <div>
          <Label className="text-xs">Loan Amount</Label>
          <Input
            type="number"
            step="0.01"
            className="h-8 text-xs font-mono"
            value={form.principal}
            onChange={(e) => setForm({ ...form, principal: e.target.value })}
            placeholder="e.g. 200000"
          />
        </div>
        <div>
          <Label className="text-xs">Annual Rate (%)</Label>
          <Input
            type="number"
            step="0.01"
            className="h-8 text-xs font-mono"
            value={form.annual_rate}
            onChange={(e) => setForm({ ...form, annual_rate: e.target.value })}
            placeholder="e.g. 3.5"
          />
        </div>
        <div>
          <Label className="text-xs">Term (months)</Label>
          <Input
            type="number"
            className="h-8 text-xs font-mono"
            value={form.term_months}
            onChange={(e) => setForm({ ...form, term_months: e.target.value })}
            placeholder="e.g. 240"
          />
        </div>
        <div>
          <Label className="text-xs">Monthly Payment</Label>
          <Input
            type="number"
            step="0.01"
            className="h-8 text-xs font-mono"
            value={form.monthly_payment}
            onChange={(e) => setForm({ ...form, monthly_payment: e.target.value })}
            placeholder="e.g. 1100"
          />
        </div>
        <div>
          <Label className="text-xs">First Payment Date</Label>
          <Input
            type="date"
            className="h-8 text-xs"
            value={form.start_date}
            onChange={(e) => setForm({ ...form, start_date: e.target.value })}
          />
        </div>
        <div>
          <Label className="text-xs">Currency</Label>
          <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CURRENCIES.map((c) => (
                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label className="text-xs">Notes (optional)</Label>
        <Input
          className="h-8 text-xs"
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          placeholder="e.g. bank name, loan reference"
        />
      </div>

      {/* Preview table */}
      {preview && (
        <div className="border rounded p-2 bg-background max-h-48 overflow-y-auto">
          <p className="text-xs font-medium mb-1">
            Schedule Preview (first 12 months) - Total interest: {formatCurrency(preview.total_interest, form.currency)}
          </p>
          <table className="w-full text-[10px]">
            <thead>
              <tr className="border-b text-muted-foreground">
                <th className="text-left py-0.5">Month</th>
                <th className="text-right py-0.5">Interest (P&L)</th>
                <th className="text-right py-0.5">Principal (cash)</th>
                <th className="text-right py-0.5">Balance</th>
              </tr>
            </thead>
            <tbody>
              {preview.schedule.slice(0, 12).map((row) => (
                <tr key={row.month_index} className="border-b border-border/30">
                  <td className="py-0.5">{row.date}</td>
                  <td className="text-right font-mono">{formatCurrency(row.interest, form.currency)}</td>
                  <td className="text-right font-mono text-muted-foreground">{formatCurrency(row.principal_portion, form.currency)}</td>
                  <td className="text-right font-mono">{formatCurrency(row.balance, form.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {preview.schedule.length > 12 && (
            <p className="text-[10px] text-muted-foreground mt-1">
              ... and {preview.schedule.length - 12} more months
            </p>
          )}
        </div>
      )}

      <div className="flex gap-2">
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={handlePreview} disabled={previewing}>
          {previewing ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
          Preview Schedule
        </Button>
        {preview && (
          <Button size="sm" className="h-7 text-xs" onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Save className="h-3 w-3 mr-1" />}
            Save Schedule
          </Button>
        )}
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setOpen(false); setPreview(null); }}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Investment editor (unchanged)
// ---------------------------------------------------------------------------

function InvestmentEditor({ propertyId, propertyCurrency }) {
  const [investment, setInvestment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    purchase_price: '',
    purchase_date: '',
    purchase_currency: propertyCurrency || 'GBP',
    acquisition_costs: '0',
    notes: '',
  });

  const fetchInvestment = useCallback(async () => {
    try {
      const res = await propertyCostsAPI.getInvestment(propertyId);
      const inv = res.data?.investment;
      setInvestment(inv);
      if (inv) {
        setForm({
          purchase_price: String(inv.purchase_price || ''),
          purchase_date: inv.purchase_date || '',
          purchase_currency: inv.purchase_currency || propertyCurrency || 'GBP',
          acquisition_costs: String(inv.acquisition_costs || '0'),
          notes: inv.notes || '',
        });
      }
    } catch (err) {
      // No investment data yet, that's fine
    } finally {
      setLoading(false);
    }
  }, [propertyId, propertyCurrency]);

  useEffect(() => {
    fetchInvestment();
  }, [fetchInvestment]);

  const handleSave = async () => {
    if (!form.purchase_price || isNaN(parseFloat(form.purchase_price))) {
      toast.error('Enter a valid purchase price');
      return;
    }
    if (!form.purchase_date) {
      toast.error('Enter a purchase date');
      return;
    }
    setSaving(true);
    try {
      await propertyCostsAPI.upsertInvestment(propertyId, {
        purchase_price: parseFloat(form.purchase_price),
        purchase_date: form.purchase_date,
        purchase_currency: form.purchase_currency,
        acquisition_costs: parseFloat(form.acquisition_costs) || 0,
        notes: form.notes || null,
      });
      toast.success('Investment data saved');
      setEditing(false);
      fetchInvestment();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  if (!editing && !investment) {
    return (
      <Button variant="outline" size="sm" className="mt-1" onClick={() => setEditing(true)}>
        <Plus className="h-3 w-3 mr-1" /> Add Investment Data
      </Button>
    );
  }

  if (!editing && investment) {
    return (
      <div className="flex items-center gap-4 text-sm">
        <span className="font-mono font-semibold">
          {formatCurrency(investment.purchase_price, investment.purchase_currency)}
        </span>
        {investment.acquisition_costs > 0 && (
          <span className="text-muted-foreground text-xs">
            + {formatCurrency(investment.acquisition_costs, investment.purchase_currency)} acq. costs
          </span>
        )}
        <span className="text-muted-foreground text-xs">{investment.purchase_date}</span>
        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setEditing(true)}>
          <PencilLine className="h-3 w-3" />
        </Button>
      </div>
    );
  }

  return (
    <div className="mt-1 p-3 border rounded-lg bg-muted/30 space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div>
          <Label className="text-xs">Purchase Price</Label>
          <Input
            type="number"
            step="0.01"
            className="h-8 text-xs font-mono"
            value={form.purchase_price}
            onChange={(e) => setForm({ ...form, purchase_price: e.target.value })}
            placeholder="0.00"
          />
        </div>
        <div>
          <Label className="text-xs">Currency</Label>
          <Select value={form.purchase_currency} onValueChange={(v) => setForm({ ...form, purchase_currency: v })}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CURRENCIES.map((c) => (
                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Purchase Date</Label>
          <Input
            type="date"
            className="h-8 text-xs"
            value={form.purchase_date}
            onChange={(e) => setForm({ ...form, purchase_date: e.target.value })}
          />
        </div>
        <div>
          <Label className="text-xs">Acquisition Costs</Label>
          <Input
            type="number"
            step="0.01"
            className="h-8 text-xs font-mono"
            value={form.acquisition_costs}
            onChange={(e) => setForm({ ...form, acquisition_costs: e.target.value })}
            placeholder="0.00"
          />
        </div>
      </div>
      <div className="flex gap-2">
        <Button size="sm" className="h-7 text-xs" onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Save className="h-3 w-3 mr-1" />}
          Save
        </Button>
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditing(false)}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main export: PropertyCostEditor (with tenure)
// ---------------------------------------------------------------------------

export function PropertyCostEditor({ propertyId, propertyCurrency, initialTenure, tenureDerived }) {
  const [costs, setCosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tenure, setTenure] = useState(initialTenure || null);
  const [isDerived, setIsDerived] = useState(tenureDerived || false);

  const fetchCosts = useCallback(async () => {
    try {
      const res = await propertyCostsAPI.listCosts(propertyId);
      setCosts(res.data?.costs || []);
    } catch (err) {
      // No costs yet, fine
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  // Fetch tenure on mount if not provided
  useEffect(() => {
    if (!initialTenure) {
      propertyCostsAPI.getTenure(propertyId).then(res => {
        if (res.data?.tenure) {
          setTenure(res.data.tenure);
          setIsDerived(res.data.tenure_derived || false);
        }
      }).catch(() => {});
    }
  }, [propertyId, initialTenure]);

  useEffect(() => {
    fetchCosts();
  }, [fetchCosts]);

  const activeCosts = costs.filter((c) => !c.effective_to || c.mortgage_schedule);
  // Rent between our own entities is not a portfolio cost (Fan, 2026-10-24).
  const portfolioCosts = activeCosts.filter((c) => !c.intra_group_rent);
  const pnlCosts = portfolioCosts.filter(c => !NON_PNL_TYPES.has(c.cost_type));
  const hasPerStayCosts = activeCosts.some(c => c.frequency === 'per_stay');
  const hasPctCosts = activeCosts.some(c => PERCENTAGE_TYPES.has(c.cost_type));
  const totalMonthlyPnl = pnlCosts
    .filter(c => !PERCENTAGE_TYPES.has(c.cost_type))
    .reduce((sum, c) => sum + (monthlyEquivalent(c.amount, c.frequency) ?? 0), 0);
  const totalMonthlyAll = portfolioCosts
    .filter(c => !PERCENTAGE_TYPES.has(c.cost_type))
    .reduce((sum, c) => sum + (monthlyEquivalent(c.amount, c.frequency) ?? 0), 0);
  const costCurrency = activeCosts.length > 0 ? activeCosts[0].currency : propertyCurrency;

  const hasMortgageEntries = activeCosts.some(c =>
    c.cost_type === 'mortgage_interest' || c.cost_type === 'mortgage_principal'
  );

  return (
    <div className="space-y-4 py-2">
      {/* Tenure (cost structure: leased = rent, owned = mortgage). Distinct from management_type (revenue arrangement). */}
      <TenureSelector
        propertyId={propertyId}
        tenure={tenure}
        onTenureChange={setTenure}
        isDerived={isDerived}
        onDerivedCleared={() => setIsDerived(false)}
      />

      {/* Recurring Costs */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <p className="text-xs font-medium text-muted-foreground">Recurring Costs</p>
          {activeCosts.length > 0 && (
            <Badge variant="secondary" className="text-xs font-mono">
              {formatCurrency(totalMonthlyPnl, costCurrency)}/mo P&L{hasPerStayCosts ? ' + per-stay' : ''}{hasPctCosts ? ' + %' : ''}
            </Badge>
          )}
          {totalMonthlyAll !== totalMonthlyPnl && (
            <Badge variant="outline" className="text-xs font-mono">
              {formatCurrency(totalMonthlyAll, costCurrency)}/mo total
            </Badge>
          )}
        </div>

        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : activeCosts.length > 0 ? (
          <table className="w-full">
            <tbody>
              {activeCosts.map((c) => (
                <CostRow
                  key={c.id}
                  cost={c}
                  propertyId={propertyId}
                  onDeleted={fetchCosts}
                />
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-xs text-muted-foreground">No recurring costs entered yet.</p>
        )}

        <AddCostForm
          propertyId={propertyId}
          propertyCurrency={propertyCurrency}
          tenure={tenure}
          onCreated={fetchCosts}
        />

        {/* Mortgage form for owned properties */}
        {tenure === 'owned' && !hasMortgageEntries && (
          <MortgageScheduleForm
            propertyId={propertyId}
            propertyCurrency={propertyCurrency}
            onCreated={fetchCosts}
          />
        )}
        {tenure === 'owned' && hasMortgageEntries && (
          <p className="text-[10px] text-muted-foreground mt-2">
            Mortgage schedule active. Interest hits P&L margin; principal is cash-flow only.
          </p>
        )}
      </div>

      {/* Investment */}
      <div>
        <p className="text-xs font-medium text-muted-foreground mb-1">Investment / Acquisition</p>
        <InvestmentEditor propertyId={propertyId} propertyCurrency={propertyCurrency} />
      </div>
    </div>
  );
}
